import React, { useEffect, useState, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  IconButton,
  TextField,
  Button,
  Tooltip,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Paper,
  Zoom,
  Chip,
  Card,
  CardContent,
  Divider,
  Stack,
  useTheme,
  alpha,
  Tabs,
  Tab,
  Icon,
} from "@mui/material";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from "@mui/icons-material/Add";
import LogoutIcon from "@mui/icons-material/Logout";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-tw';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/en';
import axiosInstance from "api/axiosInstance";
import StrategyKPICharts from "./StrategyKPICharts";
import StrategyParameterDialog from './StrategyParameterDialog';
import StrategyRevenue from "./StrategyRevenue";
import CreatorRegistrationDialog from "./CreatorRegistrationDialog";
import UpgradePlanDialog from "components/mas/UpgradePlanDialog";
import { LanguageContext } from "../../../../context/LanguageContext";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";
import { COLORS, TYPO, FONT } from "theme/tokens";

export default function StrategyManagerForm() {
  const theme = useTheme();
  const navigate = useNavigate();

  const [leasingModalOpen, setLeasingModalOpen] = useState(false);
  const [leasingTarget, setLeasingTarget] = useState({
    isLeasingOpen: false,
  });
  const [confirmRenewOpen, setConfirmRenewOpen] = useState(false);
  const [confirmDeleteLeaseOpen, setConfirmDeleteLeaseOpen] = useState(false);
  const [pendingLeaseId, setPendingLeaseId] = useState(null);
  const [simpleDeleteDialogOpen, setSimpleDeleteDialogOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [pendingDeleteStrategyId, setPendingDeleteStrategyId] = useState(null);
  const [ownStrategies, setOwnStrategies] = useState([]);
  const [rentedStrategies, setRentedStrategies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false); // 2026-06-08 編輯改跳視窗
  const [selectedReport, setSelectedReport] = useState(null);
  const [kpiOpen, setKpiOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadPct, setDownloadPct] = useState(null); // null=準備中(不定量) / 0-100=實際下載%
  const [buildStatusMap, setBuildStatusMap] = useState({});
  const [overwriteDialogOpen, setOverwriteDialogOpen] = useState(false);
  // 2026-08-27：策略沒有停損 → 後端拒絕打包。訊息是多行說明（缺什麼、怎麼補），
  //   用 snackbar 讀不完，改用對話框。
  const [noStopLossMsg, setNoStopLossMsg] = useState("");
  const [pendingStrategyId, setPendingStrategyId] = useState(null);
  const [showParamDialog, setShowParamDialog] = useState(false);
  const [selectedParam, setSelectedParam] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [isCreator, setIsCreator] = useState(false);
  const [creatorRegistrationOpen, setCreatorRegistrationOpen] = useState(false);
  const [kpiRejectDialogOpen, setKpiRejectDialogOpen] = useState(false);
  const [kpiRejectData, setKpiRejectData] = useState({ reasons: [], kpis: {} });
  const [searchQuery, setSearchQuery] = useState("");
  // 2026-06-14 FREE 匯出卡控：canExportExe=false（wizardExecLimit===0）點匯出 → 跳升級視窗
  const [canExportExe, setCanExportExe] = useState(true); // 預設 true，載入前不誤擋；後端 checkExecLimit 仍為真正 enforcement
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const isAnyProcessing = Object.values(buildStatusMap).includes("processing");
  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);
    useEffect(() => {
    loadLanguageFile("StrategyManagerForm", "strategy/StrategyManagerForm/StrategyManagerForm_lang");
    }, [language, loadLanguageFile]);

  const checkCreatorStatus = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/creator/status");
      setIsCreator(res.data.isCreator);
    } catch (err) {
      console.error("Check Creator Status Error:", err);
    }
  }, []);

  useEffect(() => {
    checkCreatorStatus();
  }, [checkCreatorStatus]);

  // 2026-06-14 抓方案旗標：是否可匯出策略 EXE（FREE=wizardExecLimit===0 → 不可）
  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get("/plans/me/plan");
        setCanExportExe(res.data?.canExportExe !== false);
      } catch (_) { /* 抓不到方案先不擋，後端 403 仍會擋並由 catch 跳升級視窗 */ }
    })();
  }, []);
  const LANGUAGE = componentLanguageData.StrategyManagerForm || { Main: {}, Error: {}, Alert: {} };

  // Set dayjs locale based on language
  const dayjsLocale = language === 'TW' ? 'zh-tw' : language === 'CN' ? 'zh-cn' : 'en';
  dayjs.locale(dayjsLocale);

  const fetchStrategies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/strategy/list");
      setOwnStrategies(res.data.own);
      setRentedStrategies(res.data.rented);
    } catch (err) {
      console.error(LANGUAGE.Error.Content1, err);
      setSnackbar({ open: true, message: LANGUAGE.Error.Content1, severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [LANGUAGE.Error.Content1]);

const handleRenewLease = async (strategyId) => {
  try {
    const response = await axiosInstance.post("/strategy/rent", { strategyId });
    
    // ✅ 若回傳 paymentHtml，表示需跳轉綠界付款
    if (response.data.paymentHtml) {
      const div = document.createElement("div");
      div.innerHTML = response.data.paymentHtml;
      document.body.appendChild(div);
      // 確保表單送出
      const form = div.querySelector("form");
      if (form) {
        form.submit();
      }
      return; 
    }

    setSnackbar({ open: true, message: LANGUAGE.Alert.Content1, severity: "success" });
    fetchStrategies();
  } catch (err) {
    console.error("Renews Error:", err);
    setSnackbar({ open: true, message: LANGUAGE.Alert.Content2, severity: "error" });
  }
};

const handleDeleteLease = async (strategyId) => {
  try {
    await axiosInstance.post("/strategy/delete-rental", { strategyId });
    setSnackbar({ open: true, message: LANGUAGE.Alert.Content3, severity: "success" });
    fetchStrategies();
  } catch (err) {
    setSnackbar({ open: true, message: LANGUAGE.Alert.Content4, severity: "error" });
  }
};


  const handleOpenKPI = (rawReport) => {
    try {
      const report = typeof rawReport === "string" ? JSON.parse(rawReport) : rawReport;
      setSelectedReport(report);
      setKpiOpen(true);
    } catch (e) {
      console.error(LANGUAGE.Error.Content2, e);
      setSnackbar({ open: true, message: LANGUAGE.Error.Content3, severity: "error" });
    }
  };

  const fetchCreatorStatus = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/creator/status");
      setIsCreator(response.data.isCreator);
    } catch (err) {
      console.error("取得創作者狀態失敗", err);
    }
  }, []);

  useEffect(() => {
    fetchStrategies();
    fetchCreatorStatus();
  }, [fetchStrategies, fetchCreatorStatus]);

  const handleStartEdit = (strategy) => {
    setEditingId(strategy.id);
    setEditingTitle(strategy.title || "");
    setEditDialogOpen(true); // 跳視窗編輯
  };

  const handleSaveEdit = async () => {
    try {
      await axiosInstance.post("/strategy/update", {
        id: editingId,
        title: editingTitle,
      });
      setEditingId(null);
      setEditDialogOpen(false);
      fetchStrategies();
      setSnackbar({ open: true, message: LANGUAGE.Error.Content4, severity: "success" });
    } catch (err) {
      console.error(LANGUAGE.Error.Content5, err);
      setSnackbar({ open: true, message: LANGUAGE.Error.Content5, severity: "error" });
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingTitle("");
  };

  const handleDelete = async (id) => {
    if (buildStatusMap[id] === "completed") {
      setPendingDeleteStrategyId(id);
      setConfirmDeleteOpen(true);
    } else {
    setPendingDeleteStrategyId(id);
    setSimpleDeleteDialogOpen(true); // 未打包 ➜ 新 Dialog
    }
  };

  const handleBuild = async (strategyId, isOutdated = false) => {
    // console.log("strategyId:",strategyId);
    try {
      const res = await axiosInstance.post(`/strategy/build-exe/${strategyId}`);

      if (res.data.status === "completed") {
        setSnackbar({ open: true, message: LANGUAGE.Error.Content6, severity: "success" });
        setBuildStatusMap(prev => ({ ...prev, [strategyId]: "completed" }));
      } else if (res.data.status === "overwrite_required") {
        setPendingStrategyId(strategyId);
        setOverwriteDialogOpen(true);
      } else if (res.data.status === "build_started" || res.data.status === "processing") {
        setSnackbar({ open: true, message: isOutdated ? (LANGUAGE.Main.BuildOutdatedSnack || "偵測到執行檔有新版本，正在為您重新打包最新版，完成後會通知您") : LANGUAGE.Error.Content7, severity: "info" });
        setBuildStatusMap(prev => ({ ...prev, [strategyId]: "processing" }));
      }
    } catch (err) {
      // FREE 後端 checkExecLimit 擋下 → 跳升級視窗（proactive 攔截失效時的保險）
      if (err?.response?.data?.code === "plan-insufficient") { setUpgradeOpen(true); return; }
      // 2026-08-27：策略沒有停損 → 後端拒絕打包，把「怎麼補」原文顯示給使用者
      if (err?.response?.data?.code === "NO_STOP_LOSS" || err?.response?.data?.code === "RECOVERY_INCOMPLETE" || err?.response?.data?.code === "LOOKBACK_TRUNCATED") {
        setNoStopLossMsg(err.response.data.error || "");
        return;
      }
      console.error(LANGUAGE.Error.Content8, err);
      setSnackbar({ open: true, message: LANGUAGE.Error.Content8, severity: "error" });
    }
  };


  // 2026-06-09 統一「匯出策略」流程：依狀態自動分流
  //  - completed         → 直接 handleDownload 拉 zip
  //  - processing        → 顯示「打包中，完成寄信通知您」snackbar
  //  - 其他（未打/失敗） → 走 handleBuild 觸發 build（含 overwrite_required modal、寄信通知由 worker 完成時做）
  const handleExport = async (strategyId) => {
    // 2026-06-14：FREE（wizardExecLimit===0）不能匯出 → 直接跳升級視窗，不打 API、不打包
    if (!canExportExe) { setUpgradeOpen(true); return; }
    // 2026-06-12：點匯出時即時抓最新打包狀態（避免拿到舊 buildStatusMap；後端會依 exe 版本回 outdated）
    let status = buildStatusMap[strategyId];
    try {
      const res = await axiosInstance.get(`/strategy/build-status/${strategyId}`);
      status = res.data.status;
      setBuildStatusMap(prev => ({ ...prev, [strategyId]: status }));
    } catch (_) { /* 抓不到就沿用既有 status */ }

    if (status === "completed") {
      return handleDownload(strategyId);
    }
    if (status === "processing") {
      setSnackbar({ open: true, message: LANGUAGE.Main.BuildProcessingSnack || "策略打包中，完成後會寄信通知您", severity: "info" });
      return;
    }
    if (status === "outdated") {
      // 版本更新 → 友善提示 + 自動重新打包（避免使用者以為「之前打過怎麼又這麼久」）
      await handleBuild(strategyId, true);
      fetchBuildStatus(strategyId);
      return;
    }
    await handleBuild(strategyId);
    // 觸發後 refresh，按鈕馬上切「打包中...」disabled，不用等下次 polling
    fetchBuildStatus(strategyId);
  };

  const handleDownload = async (strategyId) => {
    setDownloadingId(strategyId);
    setDownloadPct(null);
    try {
      const res = await axiosInstance.get(`/strategy/download-exe/${strategyId}`, {
        responseType: "blob",
        // 下載進度（沿用精靈策略下載 UX）：有 Content-Length 才有實際 %，否則維持 null 顯示不定量
        onDownloadProgress: (e) => {
          if (e.total) setDownloadPct(Math.min(100, Math.round((e.loaded / e.total) * 100)));
        },
      });
      const blob = new Blob([res.data], { type: "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `strategy_${strategyId}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      if (err?.response?.data?.code === "plan-insufficient") { setUpgradeOpen(true); return; }
      console.error(LANGUAGE.Error.Content9, err);
      setSnackbar({ open: true, message: LANGUAGE.Error.Content9, severity: "error" });
    } finally {
      setDownloadingId(null);
      setDownloadPct(null);
    }
  };


  const handleConfirmOverwrite = async () => {
    try {
      if (pendingStrategyId) {
        await axiosInstance.post(`/strategy/build-exe/${pendingStrategyId}?force=true`);
        setSnackbar({ open: true, message: LANGUAGE.Error.Content15, severity: "info" });
        await fetchBuildStatus(pendingStrategyId);
      }
    } catch (err) {
      // 2026-09-22：覆蓋打包（force=true）被閘門擋下時，原本只顯示通用錯誤 → 使用者看不到「缺什麼、怎麼補」。
      //   與第一次打包（上方 catch）接同一組錯誤碼、同一個說明對話框。
      const code = err?.response?.data?.code;
      if (code === "NO_STOP_LOSS" || code === "RECOVERY_INCOMPLETE" || code === "LOOKBACK_TRUNCATED") {
        setNoStopLossMsg(err.response.data.error || "");
        return;
      }
      console.error(LANGUAGE.Error.Content10, err);
      setSnackbar({ open: true, message: LANGUAGE.Error.Content10, severity: "error" });
    } finally {
      setOverwriteDialogOpen(false);
      setPendingStrategyId(null);
    }
  };

  const handleCancelOverwrite = () => {
    setOverwriteDialogOpen(false);
    setPendingStrategyId(null);
  };

  const handleCloseKPI = () => {
    setKpiOpen(false);
    setSelectedReport(null);
  };

  const fetchBuildStatus = useCallback(async (strategyId) => {
    try {
      const res = await axiosInstance.get(`/strategy/build-status/${strategyId}`);
      setBuildStatusMap(prev => ({ ...prev, [strategyId]: res.data.status }));
    } catch (err) {
      console.error(LANGUAGE.Error.Content11, err);
    }
  }, [LANGUAGE.Error.Content11]);

  useEffect(() => {
    [...ownStrategies, ...rentedStrategies].forEach(s => fetchBuildStatus(s.id));
  }, [ownStrategies, rentedStrategies, fetchBuildStatus]);

  const renderStrategyCard = ({
    strategy,
    idx,
    editingId,
    editingTitle,
    setEditingTitle,
    handleStartEdit,
    handleSaveEdit,
    handleDelete,
    handleBuild,
    handleDownload,
    handleOpenKPI,
    buildStatusMap,
    downloadingId,
    isAnyProcessing,
    lockedViewOnly = false,
    isRented = false,
  }) => {
    const isLocked = strategy.locked;
    const rawJson = strategy.backtestresult?.[0]?.reportJson;

    let winRate = 0, totalReturnRate = 0, totalProfit = 0;
    try {
      const parsedReport = typeof rawJson === "string" ? JSON.parse(rawJson) : rawJson;
      winRate = parsedReport?.performance_metrics?.win_rate || 0;
      totalReturnRate = parsedReport?.performance_metrics?.total_return_rate || 0;
      totalProfit = parsedReport?.general_data?.total_profit_loss ?? 0;
    } catch (e) {
      console.error("Error parsing report", e);
    }

    return (
      <Card
        elevation={0}
        sx={{
          border: "1px solid #EEE",
          borderRadius: 3,
          mb: 2,
          position: "relative",
          overflow: "hidden",
          transition: "all 0.3s ease",
          "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,0.06)", transform: "translateY(-2px)" },
        }}
      >
        {/* Left Orange Accent */}
        <Box sx={{ position: "absolute", left: 0, top: "20%", bottom: "20%", width: "4px", bgcolor: COLORS.main, borderRadius: "0 4px 4px 0" }} />

        <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
          {/* Header Row: Title */}
          <Box display="flex" alignItems="center" mb={1.5} sx={{ pl: 0.5 }}>
            <Typography sx={{ ...TYPO.title, fontWeight: 700, color: "#0A0A0A" }}>
              {strategy.title}
            </Typography>
          </Box>

          <Divider sx={{ mb: 2, opacity: 0.08 }} />

          {/* Main Info Row - all on one line */}
          <Grid container spacing={1} alignItems="center">
            {/* Tags Column */}
            <Grid item xs="auto">
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ px: 1.5, py: 0.4, bgcolor: COLORS.lighter, borderRadius: "6px", ...TYPO.bodyS, fontWeight: 400, color: COLORS.main }}>
                  {strategy.symbol}
                </Box>
                <Box sx={{ px: 1.5, py: 0.4, border: "1.5px solid", borderColor: COLORS.main, borderRadius: "6px", ...TYPO.bodyS, fontWeight: 400, color: COLORS.main }}>
                  {strategy.ordertype?.toUpperCase()}
                </Box>
                <Box sx={{ px: 1.5, py: 0.4, border: "1.5px solid", borderColor: COLORS.grayMedian, borderRadius: "6px", ...TYPO.bodyS, fontWeight: 400, color: COLORS.black }}>
                  {strategy.timeframe}
                </Box>
                <Box sx={{ px: 1.5, py: 0.4, border: "1.5px solid", borderColor: COLORS.grayMedian, borderRadius: "6px", ...TYPO.bodyS, fontWeight: 400, color: COLORS.black }}>
                  {strategy.lotSize}{LANGUAGE.Main.Content81 || "手"}
                </Box>
              </Stack>
            </Grid>

            {/* Metrics Column - 整組置中（與左邊 tag、右邊按鈕等距）*/}
            <Grid item xs sx={{ display: "flex", justifyContent: "center" }}>
              <Stack direction="row" spacing={4} alignItems="flex-start">
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 0.25 }}>
                  <Typography sx={{ ...TYPO.bodyS, fontWeight: 400, color: COLORS.black, whiteSpace: "nowrap" }}>{LANGUAGE.Main.Content80 || "總報酬 (USD)"}</Typography>
                  <Typography sx={{ ...TYPO.displayM, fontWeight: 500, color: COLORS.main, whiteSpace: "nowrap" }}>
                    {totalProfit.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 0.25 }}>
                  <Typography sx={{ ...TYPO.bodyS, fontWeight: 400, color: COLORS.black, whiteSpace: "nowrap" }}>{LANGUAGE.Main.Content5 || "總報酬率"}</Typography>
                  <Typography sx={{ ...TYPO.displayM, fontWeight: 500, color: COLORS.main, whiteSpace: "nowrap" }}>
                    {totalReturnRate.toFixed(2)}%
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 0.25 }}>
                  <Typography sx={{ ...TYPO.bodyS, fontWeight: 400, color: COLORS.black, whiteSpace: "nowrap" }}>{LANGUAGE.Main.Content6 || "勝率"}</Typography>
                  <Typography sx={{ ...TYPO.displayM, fontWeight: 500, color: COLORS.main, whiteSpace: "nowrap" }}>
                    {winRate.toFixed(2)}%
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            {/* Actions Column — 順序：刪除 / 編輯 / 查看詳情 / 匯出策略（依截圖一）*/}
            <Grid item xs="auto">
              <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                {/* 刪除 */}
                {!lockedViewOnly && !isRented && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleDelete(strategy.id)}
                    sx={{ borderRadius: "8px", borderColor: COLORS.error, color: COLORS.error, textTransform: "none", ...TYPO.bodyS, fontWeight: 400, whiteSpace: "nowrap", "&:hover": { borderColor: COLORS.error, bgcolor: "rgba(235,49,25,0.05)" } }}
                  >
                    {LANGUAGE.Main.Content91 || "刪除"}
                  </Button>
                )}

                {/* 編輯（跳視窗）*/}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleStartEdit(strategy)}
                  sx={{ borderRadius: "8px", borderColor: COLORS.grayMedian, color: COLORS.black, textTransform: "none", ...TYPO.bodyS, fontWeight: 400 }}
                >
                  {LANGUAGE.Main.Content92 || "編輯"}
                </Button>

                {/* 查看詳情 + eye icon */}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<img src="/images/icon/genie/eye-open.png" alt="" style={{ width: 16, height: 16, objectFit: "contain" }} />}
                  onClick={() => handleOpenKPI(strategy.backtestresult?.[0]?.reportJson)}
                  sx={{ borderRadius: "8px", borderColor: COLORS.grayMedian, color: COLORS.black, textTransform: "none", ...TYPO.bodyS, fontWeight: 400, whiteSpace: "nowrap" }}
                >
                  {LANGUAGE.Main.Content93 || "查看詳情"}
                </Button>

                {/* 匯出策略 */}
                {(() => {
                  const myStatus = buildStatusMap[strategy.id];
                  const isMyBuilding = myStatus === "processing";
                  // 後端 per-user lock：同時間只能跑一支 build；別支在 build 時這支也要鎖
                  const isAnyBuilding = Object.values(buildStatusMap).some(s => s === "processing");
                  const isLocked = isMyBuilding || isAnyBuilding;
                  const label = isMyBuilding
                    ? (LANGUAGE.Main.BtnBuildProcessing || "⏳ 打包中...")
                    : isAnyBuilding
                      ? (LANGUAGE.Main.BtnOtherBuilding || "⏳ 其他策略打包中")
                      : (LANGUAGE.Main.Content94 || "匯出策略");
                  return (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<LogoutIcon sx={{ fontSize: "0.9rem !important" }} />}
                      onClick={() => handleExport(strategy.id)}
                      disabled={isLocked}
                      sx={{
                        borderRadius: "8px",
                        borderColor: COLORS.main,
                        color: COLORS.main,
                        textTransform: "none",
                        ...TYPO.bodyS,
                        fontWeight: 400,
                        whiteSpace: "nowrap",
                        "&:hover": { borderColor: COLORS.main, bgcolor: "rgba(243, 109, 38, 0.05)" }
                      }}
                    >
                      {label}
                    </Button>
                  );
                })()}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: 3, bgcolor: COLORS.pageBg, minHeight: "100%" }}>
      {/* Page Title */}
      <Typography variant="h5" fontWeight={700} color={COLORS.black} mb={3}>
        {LANGUAGE.Main.Content21 || "策略管理"}
      </Typography>

      {/* 2026-06-08 移除多餘的「我的策略」分頁按鈕（Sean 要求）*/}

      {/* Main Content - Full Width */}
      <Box sx={{ width: "100%" }}>
          {/* 2026-05-25 移除總策略數/上架/未上架三個統計 block（依 Sean 要求）*/}

          {/* Search Bar — 2026-05-25 依設計稿規格：高 52、placeholder 灰 #C4C4C4 Noto Sans TC，
              右側灰底 48x52 #F6F6F6 內含 Search.png 24x24 */}
          <Box mb={3}>
            <TextField
              fullWidth
              placeholder={LANGUAGE.Main.Content95 || "搜尋關鍵字..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 52,
                  borderRadius: "10px",
                  bgcolor: COLORS.white,
                  pr: 0,
                  overflow: "hidden",
                  "& fieldset": { borderColor: "#EBEBEB" },
                  "&:hover fieldset": { borderColor: "#DDD" },
                  "&.Mui-focused fieldset": { borderColor: COLORS.main, borderWidth: "1px" },
                },
                "& .MuiOutlinedInput-input": {
                  fontFamily: 'var(--cjk-font, "Noto Sans TC"), sans-serif',
                  fontSize: "14px",
                  fontWeight: 400,
                  lineHeight: "21px",
                  "&::placeholder": {
                    color: COLORS.grayMedian,
                    opacity: 1,
                  },
                },
              }}
              InputProps={{
                endAdornment: (
                  <Box
                    sx={{
                      width: 48,
                      height: 52,
                      px: "12px",
                      py: "14px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      flexShrink: 0,
                      background: COLORS.grayLightest,
                    }}
                  >
                    <Box
                      component="img"
                      src="/images/icon/Search.png"
                      alt="search"
                      sx={{ width: 24, height: 24, p: "2px", objectFit: "contain" }}
                    />
                  </Box>
                ),
              }}
            />
          </Box>
          <Box sx={{ pb: 6 }}>
            {ownStrategies
              .filter(s => 
                s.title?.toLowerCase().includes(searchQuery.toLowerCase()) || 
                s.symbol?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                s.timeframe?.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .length === 0 && rentedStrategies.length === 0 ? (
              <Box textAlign="center" py={10}>
                <Box
                  component="img"
                  src="/images/icon/Browser Search.png"
                  alt=""
                  sx={{ width: 120, height: 120, objectFit: "contain" }}
                />
                <Typography variant="h6" mt={2} color="text.secondary">
                  {LANGUAGE.Main.Content34 || "尚未建立任何策略，請前往"}
                </Typography>
                {/* 原生 button：避開 MUI Button text 變體內建 hover 底色，確保滑過不變色 */}
                <button
                  type="button"
                  onClick={() => navigate("/strategy-summary")}
                  style={{
                    marginTop: 24,
                    padding: "12px 32px",
                    minHeight: 44,
                    border: "none",
                    borderRadius: 8,
                    background: COLORS.main,
                    color: COLORS.white,
                    fontFamily: FONT.NOTO,
                    fontSize: 16,
                    fontWeight: 500,
                    lineHeight: 1,
                    cursor: "pointer",
                  }}
                >
                  {LANGUAGE.Main.Content35 || "AI 策略助理"}
                </button>
              </Box>
            ) : (
              <Stack spacing={2}>
                {ownStrategies
                  .filter(s => 
                    s.title?.toLowerCase().includes(searchQuery.toLowerCase()) || 
                    s.symbol?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    s.timeframe?.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map((strategy, idx) => (
                    <Zoom in key={strategy.id} style={{ transitionDelay: `${idx * 50}ms` }}>
                      <Box>
                        {renderStrategyCard({
                          strategy,
                          idx,
                          editingId,
                          editingTitle,
                          setEditingTitle,
                          handleStartEdit,
                          handleSaveEdit,
                          handleDelete,
                          handleBuild,
                          handleDownload,
                          handleOpenKPI,
                          buildStatusMap,
                          downloadingId,
                          isAnyProcessing,
                        })}
                      </Box>
                    </Zoom>
                  ))}

                {rentedStrategies.length > 0 && (
                  <>
                    <Typography variant="h6" fontWeight={700} mt={4} mb={2} color="text.secondary">
                      {LANGUAGE.Main.Content64 || "租賃策略"}
                    </Typography>
                    {rentedStrategies
                      .filter(s => 
                        s.title?.toLowerCase().includes(searchQuery.toLowerCase()) || 
                        s.symbol?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        s.timeframe?.toLowerCase().includes(searchQuery.toLowerCase())
                      )
                      .map((strategy, idx) => (
                        <Zoom in key={strategy.id} style={{ transitionDelay: `${idx * 50}ms` }}>
                          <Box>
                            {renderStrategyCard({
                              strategy,
                              idx,
                              editingId,
                              editingTitle,
                              setEditingTitle,
                              handleStartEdit,
                              handleSaveEdit,
                              handleDelete,
                              handleBuild,
                              handleDownload,
                              handleOpenKPI,
                              buildStatusMap,
                              downloadingId,
                              isAnyProcessing,
                              lockedViewOnly: strategy.isLeaseExpired,
                              isRented: true,
                            })}
                          </Box>
                        </Zoom>
                      ))}
                  </>
                )}
              </Stack>
            )}
          </Box>
      </Box>


      <Dialog open={kpiOpen} onClose={handleCloseKPI} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{LANGUAGE.Main.Content39}</DialogTitle>
        <DialogContent dividers>
          {selectedReport ? <StrategyKPICharts data={selectedReport} /> : LANGUAGE.Main.Content55}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseKPI}>{LANGUAGE.Main.Content40}</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!noStopLossMsg}
        onClose={() => setNoStopLossMsg("")}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>⚠️ 無法打包</DialogTitle>
        <DialogContent>
          {/* 後端訊息是多行純文字（含「怎麼補」的三種寫法），保留換行原樣顯示 */}
          <Typography sx={{ whiteSpace: "pre-line" }}>{noStopLossMsg}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" sx={{ color: COLORS.white }} onClick={() => setNoStopLossMsg("")}>
            我知道了
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={overwriteDialogOpen} onClose={handleCancelOverwrite} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{LANGUAGE.Main.Content41}</DialogTitle>
        <DialogContent>
          <Typography>
            {LANGUAGE.Main.Content42}<br />
            {LANGUAGE.Main.Content43}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
           variant="contained"
           sx={{color:COLORS.white}}
           onClick={handleCancelOverwrite}>{LANGUAGE.Main.Content48}</Button>
          <Button
            variant="contained"
            color="primary"
            sx={{color:COLORS.white, borderColor:"#b40000ff",backgroundColor:"#b40000ff","&:hover": {
                        borderColor:"#b40000ff",backgroundColor:"#b40000ff",color:COLORS.white
                      },}}
            onClick={handleConfirmOverwrite}
          >
            {LANGUAGE.Main.Content49}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={simpleDeleteDialogOpen}
        onClose={() => setSimpleDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            width: "700px",
            maxWidth: "700px",
            borderRadius: "24px",
            backgroundColor: COLORS.white,
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          },
        }}
      >
        <DialogTitle
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 3,
            fontFamily: FONT.NOTO,
            fontSize: "24px",
            fontWeight: 700,
            lineHeight: "42px",
            color: "#2C2C2C",
          }}
        >
          {LANGUAGE.Main.Content97 || "刪除此策略"}
          <IconButton onClick={() => setSimpleDeleteDialogOpen(false)} size="small" sx={{ color: COLORS.grayDark }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ width: "100%", px: 3, py: 1 }}>
          <Typography sx={{ ...TYPO.title, fontWeight: 500, lineHeight: "27px", color: COLORS.black, mb: 1 }}>
            {(LANGUAGE.Main.Content46 || "確認刪除此策略").replace(/^[^一-鿿A-Za-z]+/, "")}
          </Typography>
          <Typography sx={{ ...TYPO.bodyM, fontWeight: 400, lineHeight: "24px", color: COLORS.red }}>
            {LANGUAGE.Main.Content47}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ width: "100%", justifyContent: "flex-end", gap: "10px", px: 3, pb: 3, pt: 2 }}>
          <Button
            disableElevation
            onClick={() => setSimpleDeleteDialogOpen(false)}
            sx={{ height: 48, px: 4, py: 1.5, borderRadius: "8px", bgcolor: COLORS.grayLightest, color: COLORS.black, ...TYPO.title, fontWeight: 500, lineHeight: "27px", textTransform: "none", boxShadow: "none", "&:hover": { bgcolor: COLORS.grayLightest, color: COLORS.black, boxShadow: "none" } }}
          >
            {LANGUAGE.Main.Content48 || "取消"}
          </Button>
          <Button
            disableElevation
            onClick={async () => {
              try {
                await axiosInstance.post("/strategy/delete", { id: pendingDeleteStrategyId });
                fetchStrategies();
                setSnackbar({ open: true, message: LANGUAGE.Error.Content13, severity: "success" });
              } catch (err) {
                console.error(LANGUAGE.Error.Content14, err);
                setSnackbar({ open: true, message: LANGUAGE.Error.Content14, severity: "error" });
              } finally {
                setSimpleDeleteDialogOpen(false);
                setPendingDeleteStrategyId(null);
              }
            }}
            sx={{ height: 48, px: 4, py: 1.5, borderRadius: "8px", bgcolor: COLORS.red, color: COLORS.white, ...TYPO.title, fontWeight: 500, lineHeight: "27px", textTransform: "none", boxShadow: "none", "&:hover": { bgcolor: COLORS.red, color: COLORS.white, boxShadow: "none" } }}
          >
            {LANGUAGE.Main.Content54 || "確定刪除"}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        PaperProps={{
          sx: {
            width: "700px",
            maxWidth: "700px",
            borderRadius: "24px",
            backgroundColor: COLORS.white,
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          },
        }}
      >
        <DialogTitle
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 3,
            fontFamily: FONT.NOTO,
            fontSize: "24px",
            fontWeight: 700,
            lineHeight: "42px",
            color: "#2C2C2C",
          }}
        >
          {LANGUAGE.Main.Content97 || "刪除此策略"}
          <IconButton onClick={() => setConfirmDeleteOpen(false)} size="small" sx={{ color: COLORS.grayDark }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ width: "100%", px: 3, py: 1 }}>
          <Typography sx={{ ...TYPO.title, fontWeight: 500, lineHeight: "27px", color: COLORS.black, mb: 1 }}>
            {LANGUAGE.Main.Content51}
          </Typography>
          <Typography sx={{ ...TYPO.bodyM, fontWeight: 400, lineHeight: "24px", color: COLORS.red }}>
            {LANGUAGE.Main.Content52}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ width: "100%", justifyContent: "flex-end", gap: "10px", px: 3, pb: 3, pt: 2 }}>
          <Button
            disableElevation
            onClick={() => setConfirmDeleteOpen(false)}
            sx={{ height: 48, px: 4, py: 1.5, borderRadius: "8px", bgcolor: COLORS.grayLightest, color: COLORS.black, ...TYPO.title, fontWeight: 500, lineHeight: "27px", textTransform: "none", boxShadow: "none", "&:hover": { bgcolor: COLORS.grayLightest, color: COLORS.black, boxShadow: "none" } }}
          >
            {LANGUAGE.Main.Content53 || "取消"}
          </Button>
          <Button
            disableElevation
            onClick={async () => {
              try {
                await axiosInstance.post("/strategy/delete", { id: pendingDeleteStrategyId });
                fetchStrategies();
                setSnackbar({ open: true, message: LANGUAGE.Error.Content13, severity: "success" });
              } catch (err) {
                console.error(LANGUAGE.Error.Content14, err);
                setSnackbar({ open: true, message: LANGUAGE.Error.Content14, severity: "error" });
              } finally {
                setConfirmDeleteOpen(false);
                setPendingDeleteStrategyId(null);
              }
            }}
            sx={{ height: 48, px: 4, py: 1.5, borderRadius: "8px", bgcolor: COLORS.red, color: COLORS.white, ...TYPO.title, fontWeight: 500, lineHeight: "27px", textTransform: "none", boxShadow: "none", "&:hover": { bgcolor: COLORS.red, color: COLORS.white, boxShadow: "none" } }}
          >
            {LANGUAGE.Main.Content54 || "確定刪除"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 2026-06-08 編輯策略名稱視窗（樣式同刪除，確認鈕橘色 #F36D26）*/}
      <Dialog
        open={editDialogOpen}
        onClose={() => { setEditDialogOpen(false); setEditingId(null); }}
        PaperProps={{
          sx: {
            width: "700px",
            maxWidth: "700px",
            borderRadius: "24px",
            backgroundColor: COLORS.white,
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          },
        }}
      >
        <DialogTitle
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 3,
            fontFamily: FONT.NOTO,
            fontSize: "24px",
            fontWeight: 700,
            lineHeight: "42px",
            color: "#2C2C2C",
          }}
        >
          {LANGUAGE.Main.Content92 || "編輯"}
          <IconButton onClick={() => { setEditDialogOpen(false); setEditingId(null); }} size="small" sx={{ color: COLORS.grayDark }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ width: "100%", px: 3, py: 1 }}>
          <Typography sx={{ ...TYPO.bodyS, fontWeight: 400, lineHeight: "21px", color: COLORS.black, mb: 1 }}>
            {LANGUAGE.Main.Content2 || "策略名稱"}<span style={{ color: COLORS.error }}>*</span>
          </Typography>
          <TextField
            fullWidth
            autoFocus
            value={editingTitle}
            onChange={(e) => setEditingTitle(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
                backgroundColor: COLORS.grayLightest,
                "& fieldset": { border: "none" },
                "&:hover fieldset": { border: "none" },
                "&.Mui-focused fieldset": { border: "none" },
              },
              "& .MuiOutlinedInput-input": {
                ...TYPO.bodyM,
                lineHeight: "24px",
                color: COLORS.black,
                padding: "10px 12px",
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ width: "100%", justifyContent: "flex-end", gap: "10px", px: 3, pb: 3, pt: 2 }}>
          <Button
            disableElevation
            onClick={() => { setEditDialogOpen(false); setEditingId(null); }}
            sx={{ height: 48, px: 4, py: 1.5, borderRadius: "8px", bgcolor: COLORS.grayLightest, color: COLORS.black, ...TYPO.title, fontWeight: 500, lineHeight: "27px", textTransform: "none", boxShadow: "none", "&:hover": { bgcolor: COLORS.grayLightest, color: COLORS.black, boxShadow: "none" } }}
          >
            {LANGUAGE.Main.Content83 || "取消"}
          </Button>
          <Button
            disableElevation
            onClick={handleSaveEdit}
            sx={{ height: 48, px: 4, py: 1.5, borderRadius: "8px", bgcolor: COLORS.main, color: COLORS.white, ...TYPO.title, fontWeight: 500, lineHeight: "27px", textTransform: "none", boxShadow: "none", "&:hover": { bgcolor: COLORS.main, color: COLORS.white, boxShadow: "none" } }}
          >
            {LANGUAGE.Main.Content96 || "確認"}
          </Button>
        </DialogActions>
      </Dialog>

      {showParamDialog && selectedParam && (
        <StrategyParameterDialog
          open={showParamDialog}
          onClose={() => setShowParamDialog(false)}
          parameters={selectedParam}
        />
      )}

      <Dialog 
        open={leasingModalOpen} 
        onClose={() => setLeasingModalOpen(false)} 
        PaperProps={{ 
          sx: { 
            borderRadius: 3,
            width: "100%",
            maxWidth: "600px",
            p: 1
          } 
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" p={2} pb={1}>
          <MDTypography variant="h5" fontWeight="medium">
            {LANGUAGE.Main.Content65}
          </MDTypography>
          <IconButton onClick={() => setLeasingModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Divider sx={{ my: 1 }} />

        <DialogContent sx={{ p: 3 }}>
          <Box mb={3} display="flex" alignItems="center" justifyContent="space-between" bgcolor={alpha(theme.palette.info.main, 0.05)} p={2} borderRadius={2}>
            <Box>
              <MDTypography variant="subtitle2" fontWeight="bold">
                {LANGUAGE.Main.Content2}：{leasingTarget?.title}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                {leasingTarget?.isLeasingOpen ? LANGUAGE.Main.Content58 : LANGUAGE.Main.Content59}
              </MDTypography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={leasingTarget?.isLeasingOpen || false}
                  onChange={(e) =>
                    setLeasingTarget({ ...leasingTarget, isLeasingOpen: e.target.checked })
                  }
                  color="info"
                />
              }
              label=""
              sx={{ m: 0 }}
            />
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={LANGUAGE.Main.Content66}
                type="number"
                disabled={!leasingTarget?.isLeasingOpen}
                value={leasingTarget?.leasingPrice || ""}
                onChange={(e) => setLeasingTarget({ ...leasingTarget, leasingPrice: e.target.value })}
                InputProps={{
                  startAdornment: <Typography variant="caption" mr={1}>$</Typography>
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={LANGUAGE.Main.Content67}
                type="number"
                disabled={!leasingTarget?.isLeasingOpen}
                value={leasingTarget?.discountPrice || ""}
                onChange={(e) => setLeasingTarget({ ...leasingTarget, discountPrice: e.target.value })}
                InputProps={{
                  startAdornment: <Typography variant="caption" mr={1}>$</Typography>
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsLocale}>
                <DatePicker
                  label={LANGUAGE.Main.Content68}
                  disabled={!leasingTarget?.isLeasingOpen}
                  value={leasingTarget?.discountStart ? dayjs(leasingTarget.discountStart) : null}
                  onChange={(newValue) => {
                    setLeasingTarget({ 
                      ...leasingTarget, 
                      discountStart: newValue ? newValue.hour(0).minute(0).format('YYYY-MM-DDTHH:mm') : '' 
                    });
                  }}
                  format="YYYY-MM-DD"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsLocale}>
                <DatePicker
                  label={LANGUAGE.Main.Content69}
                  disabled={!leasingTarget?.isLeasingOpen}
                  value={leasingTarget?.discountEnd ? dayjs(leasingTarget.discountEnd) : null}
                  onChange={(newValue) => {
                    setLeasingTarget({ 
                      ...leasingTarget, 
                      discountEnd: newValue ? newValue.hour(0).minute(0).format('YYYY-MM-DDTHH:mm') : '' 
                    });
                  }}
                  format="YYYY-MM-DD"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <MDButton 
            variant="text" 
            color="secondary" 
            onClick={() => setLeasingModalOpen(false)}
          >
            {LANGUAGE.Main.Content53}
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            onClick={async () => {
              try {
                await axiosInstance.post("/strategy/set-leasing-setting", {
                  strategyId: leasingTarget.id,
                  price: leasingTarget.leasingPrice,
                  discountPrice: leasingTarget.discountPrice || null,
                  discountStart: leasingTarget.discountPrice ? leasingTarget.discountStart : null,
                  discountEnd: leasingTarget.discountPrice ? leasingTarget.discountEnd : null,
                  isLeasingOpen: leasingTarget.isLeasingOpen || false,
                });
                setSnackbar({ open: true, message: LANGUAGE.Main.Content70, severity: "success" });
                setLeasingModalOpen(false);
                fetchStrategies();
              } catch (err) {
                if (err.response?.status === 400 && err.response?.data?.reasons) {
                  setKpiRejectData({
                    reasons: err.response.data.reasons,
                    kpis: err.response.data.kpis
                  });
                  setKpiRejectDialogOpen(true);
                  setLeasingModalOpen(false);
                } else {
                  setSnackbar({ open: true, message: err.response?.data?.message || LANGUAGE.Main.Content71, severity: "error" });
                }
              }
            }}
          >
            {LANGUAGE.Main.Content78}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmRenewOpen} onClose={() => setConfirmRenewOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{LANGUAGE.Main.Content72}</DialogTitle>
        <DialogContent>
          <Typography>{LANGUAGE.Main.Content73}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRenewOpen(false)}>{LANGUAGE.Main.Content53}</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              await handleRenewLease(pendingLeaseId);
              setConfirmRenewOpen(false);
              setPendingLeaseId(null);
            }}
          >
            {LANGUAGE.Main.Content74}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDeleteLeaseOpen} onClose={() => setConfirmDeleteLeaseOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{LANGUAGE.Main.Content75}</DialogTitle>
        <DialogContent>
          <Typography>{LANGUAGE.Main.Content76}</Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            variant="contained"
            sx={{color:COLORS.white}}
            onClick={() => setConfirmDeleteLeaseOpen(false)}>{LANGUAGE.Main.Content48}
          </Button>
          <Button
            variant="contained"
            color="error"
            sx={{color:COLORS.white, borderColor:"#b40000ff",backgroundColor:"#b40000ff","&:hover": {
                        borderColor:"#b40000ff",backgroundColor:"#b40000ff",color:COLORS.white
                      },}}
            onClick={async () => {
              await handleDeleteLease(pendingLeaseId);
              setConfirmDeleteLeaseOpen(false);
              setPendingLeaseId(null);
            }}
          >
            {LANGUAGE.Main.Content77}
          </Button>
        </DialogActions>
      </Dialog>


      <UpgradePlanDialog open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />

      {/* 2026-06-14：下載進度視窗（沿用精靈策略下載 UX）。無關閉鈕，下載完(finally)自動關，避免誤關以為取消 */}
      <Dialog
        open={downloadingId !== null}
        PaperProps={{ sx: { borderRadius: 3, width: 360, maxWidth: "90vw", m: 2 } }}
      >
        <DialogContent sx={{ p: "32px 28px", display: "flex", flexDirection: "column", alignItems: "center", gap: "14px" }}>
          <img src="/images/Loading.gif" alt="" style={{ width: 96, height: 96, objectFit: "contain" }} />
          <span style={{ ...TYPO.headingM, color: COLORS.black, textAlign: "center" }}>
            {downloadPct == null
              ? (LANGUAGE.Main.DlPreparing || "正在準備您的下載檔案…")
              : `${LANGUAGE.Main.Downloading || "正在下載…"} ${downloadPct}%`}
          </span>
          <span style={{ ...TYPO.bodyM, color: COLORS.grayDark, textAlign: "center", lineHeight: "21px" }}>
            {LANGUAGE.Main.DlHint || "檔案較大時可能需要幾秒鐘，請勿關閉視窗"}
          </span>
          <LinearProgress
            variant={downloadPct == null ? "indeterminate" : "determinate"}
            value={downloadPct == null ? undefined : downloadPct}
            sx={{
              width: "100%", height: 8, borderRadius: 999, backgroundColor: "#F0F0F0",
              "& .MuiLinearProgress-bar": { backgroundColor: COLORS.main, borderRadius: 999 },
            }}
          />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ 
            width: "100%",
            borderRadius: 2,
            boxShadow: 3
           }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <CreatorRegistrationDialog 
        open={creatorRegistrationOpen} 
        onClose={() => setCreatorRegistrationOpen(false)}
        onSuccess={() => {
          setIsCreator(true);
          setSnackbar({ open: true, message: LANGUAGE.Main.Content88, severity: "success" });
        }}
      />

      <Dialog 
        open={kpiRejectDialogOpen} 
        onClose={() => setKpiRejectDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 500 } }}
      >
        <DialogTitle sx={{ color: "error.main", fontWeight: "bold" }}>{LANGUAGE.Main.Content84}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {LANGUAGE.Main.Content85}
          </Typography>
          <Box component="ul" sx={{ mt: 2, mb: 2, pl: 2 }}>
            {kpiRejectData.reasons.map((reason, i) => (
              <li key={i}><Typography variant="body2" color="error">{reason}</Typography></li>
            ))}
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="textSecondary">
            {LANGUAGE.Main.Content86}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKpiRejectDialogOpen(false)}>{LANGUAGE.Main.Content87}</Button>
        </DialogActions>
      </Dialog>
    </Box>
    
  );
}
